import * as Sentry from '@sentry/react';
import { STAGE, StageEnum } from './stage';

interface SentryConfig {
  enabled: boolean;
  environment: string;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  attachStacktrace: boolean;
}

const sentryProduction: SentryConfig = {
  enabled: true,
  environment: StageEnum.Production,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  attachStacktrace: true,
};

const getSentryConfig = (): SentryConfig => {
  if (STAGE === StageEnum.Production) {
    return sentryProduction;
  }
  return {
    enabled: false,
    environment: STAGE,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    attachStacktrace: true,
  };
};

const SENTRY_DSN =
  'https://52b66315dbb4ca6df7005ea77182c022@sentry-tech.cdek.tech/440';

const tracePropagationTargets = [/^\/api/];

const beforeSend = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null => {
  const sentryConfig = getSentryConfig();

  if (!sentryConfig.enabled) {
    return null;
  }

  if (event.exception) {
    const error = hint.originalException;

    if (error && typeof error === 'object' && 'message' in error) {
      const message = String(error.message);
      if (
        message.includes('Non-Error promise rejection captured') ||
        message.includes('ResizeObserver loop limit exceeded') ||
        message.includes('NetworkError') ||
        message.includes('Failed to fetch')
      ) {
        return null;
      }
    }
  }

  return event;
};

export const initSentry = (release: string) => {
  const sentryConfig = getSentryConfig();

  if (!sentryConfig.enabled) {
    console.info(`Sentry doesn't send errors for environment: ${STAGE}`);
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: sentryConfig.environment,
    release: release || 'dev',

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    tracePropagationTargets,

    // 0 - не отправляем данные о запросах в Sentry и отключаем добавление заголовков sentry-trace и baggage
    tracesSampleRate: 0,

    replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,

    beforeSend,

    attachStacktrace: sentryConfig.attachStacktrace,
  });
};
